import { memo, useState, useCallback, useMemo } from 'react';
import { RotateCcw, Maximize2, Minus, Plus } from 'lucide-react';
import { useQuizTheme, answerTheme, marqueSigne } from './quizTheme';
import PleinEcranInteractif from './PleinEcranInteractif';
import { etatsPoints } from './corrige';

/**
 * Droite graduée interactive — l'élève place des points.
 * Vue normale et vue « Agrandir » (plein écran) : tap (clic) sur la graduation
 * pour placer le point actif + boutons ±step pour ajustement fin (utile mobile).
 * Avancement séquentiel automatique au tap. Format de réponse identique aux deux
 * modes → scoring inchangé.
 *
 * @param {Object} numberline - Config de la droite { start, end, step }
 * @param {Array<string>} pointsToPlace - Labels à placer (ex: ["X", "Y", "Z"])
 * @param {string} selectedValue - Réponse existante (JSON string, restauration navigation)
 * @param {Function} onAnswer - Callback (jsonString) à chaque changement
 * @param {Function} onComplete - Callback (bool) quand tous les points sont placés
 */
// Relit une réponse JSON ('{"A":"1,5"}' — valeurs en virgule française, format
// émis par emitAnswer) et reconvertit en nombres. Sans ça, l'aller-retour
// réponse → selectedValue → resync réinjecte des STRINGS dans placedPoints :
// toX("1,5") = NaN → cx invalide → le point se rend à x=0, hors champ.
const parsePlacedPoints = (json) => {
    try {
        const obj = JSON.parse(json);
        const out = {};
        Object.entries(obj).forEach(([label, v]) => {
            const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
            if (!Number.isNaN(n)) out[label] = n;
        });
        return out;
    } catch { return {}; }
};

const COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899'];
// Correction : les tons des classes juste / faux d'answerTheme (les couleurs
// de points ci-dessus comptent un rouge et un vert, elles s'effacent alors).
const CORRECTION = { juste: '#10b981', faux: '#ef4444' };

// Tailles SVG selon le mode (normal vs plein écran)
const SIZES = {
    normal: { H: 100, lineY: 50, r: 5, tick: 4, tickBig: 6, txt: 10, lbl: 11, line: 1.5 },
    big: { H: 170, lineY: 95, r: 13, tick: 8, tickBig: 12, txt: 22, lbl: 24, line: 2.5 },
};

const NumberLineInteractiveEleve = memo(function NumberLineInteractiveEleve({
    numberline, pointsToPlace = [], selectedValue, onAnswer, onComplete, corrige = null,
}) {
    const clair = useQuizTheme() === 'clair';
    const t = answerTheme(clair);
    const { start = 0, end = 10, step = 1, reperes = [] } = numberline || {};

    // ─── Correction sur place (lot 6, 2026-09-21) ───
    // `corrige` n'arrive que si la réponse est fausse (contrat dans corrige.jsx).
    // Ses points restent où il les a mis, en vert ou en rouge ; pour un point
    // faux, la position attendue apparaît en pointillé vert avec son nom, sous
    // la droite. Les pastilles de noms prennent la coche ou la croix. Plus de
    // consigne, de fantôme, d'« Agrandir » ni de « Réinitialiser » : la droite
    // ne réagit plus. Ce qui s'affiche est ce que Quiz a jugé, pas l'état local.
    // Sans `corrige`, le rendu d'avant.
    const etats = useMemo(() => (corrige ? etatsPoints(corrige) : null), [corrige]);
    const pointsCorriges = useMemo(() => (corrige ? parsePlacedPoints(corrige.reponseEleve) : null), [corrige]);

    // ─── Points placés : { label → valeur numérique } ───
    const [placedPoints, setPlacedPoints] = useState(() => (
        selectedValue ? parsePlacedPoints(selectedValue) : {}
    ));

    // Label actif — initialisé au premier point non placé (au montage uniquement).
    // ⚠ PAS d'auto-avancement réactif sur placedPoints : sinon les ± (qui modifient
    // placedPoints) feraient sauter au point suivant. L'avancement séquentiel ne se
    // fait qu'au TAP, explicitement.
    const [activeLabel, setActiveLabel] = useState(() => {
        const initial = selectedValue ? parsePlacedPoints(selectedValue) : {};
        return pointsToPlace.find(l => initial[l] === undefined) || null;
    });

    // Valeur snappée sous le curseur — pilote le point fantôme de prévisualisation
    const [hoverVal, setHoverVal] = useState(null);

    // Plein écran (placement tactile confortable avec ajustement ±)
    const [isExpanded, setIsExpanded] = useState(false);

    // ─── SVG dimensions (largeur identique à NumberLineElementEleve) ───
    const W = 600;
    const padL = 25, padR = 35;
    const usableW = W - padL - padR;
    const toX = useCallback((val) => padL + ((val - start) / (end - start)) * usableW, [start, end, usableW]);
    const toVal = useCallback((x) => start + ((x - padL) / usableW) * (end - start), [start, end, usableW]);

    // Snap au step le plus proche
    const snapToStep = useCallback((val) => {
        const snapped = Math.round(val / step) * step;
        return Math.max(start, Math.min(end, Math.round(snapped * 1000) / 1000));
    }, [start, end, step]);

    // Graduations
    const graduations = useMemo(() => {
        if (step <= 0 || start >= end) return [];
        const grads = [];
        for (let i = start; i <= end + step / 2; i += step) {
            grads.push(Math.round(i * 1000) / 1000);
            if (grads.length > 100) break;
        }
        return grads;
    }, [start, end, step]);

    const reperesSet = useMemo(() => new Set(reperes.map(v => Math.round(v * 1000))), [reperes]);

    // ─── Émettre la réponse (format inchangé : {"A":"1,5"} en virgule FR) ───
    const emitAnswer = useCallback((points) => {
        const obj = {};
        Object.entries(points).forEach(([label, val]) => {
            obj[label] = String(val).replace('.', ',');
        });
        onAnswer(JSON.stringify(obj));
        onComplete(pointsToPlace.every(l => points[l] !== undefined));
    }, [pointsToPlace, onAnswer, onComplete]);

    // Position pixel d'un événement → valeur snappée (utilise le <svg> porteur du handler).
    const valFromEvent = useCallback((e) => {
        const svg = e.currentTarget;
        if (!svg) return null;
        const rect = svg.getBoundingClientRect();
        if (!rect.width) return null;
        const scaleX = W / rect.width;
        const clickX = (e.clientX - rect.left) * scaleX;
        return snapToStep(toVal(clickX));
    }, [toVal, snapToStep]);

    // ─── Vue normale : clic = placer le point actif ───
    const handleSvgClick = useCallback((e) => {
        if (!activeLabel) return;
        const val = valFromEvent(e);
        if (val === null) return;
        setHoverVal(null);
        const next = { ...placedPoints, [activeLabel]: val };
        setPlacedPoints(next);
        emitAnswer(next);
        // Vue normale uniquement : avancer au prochain point non placé (placement séquentiel rapide)
        const nextUnplaced = pointsToPlace.find(l => next[l] === undefined);
        setActiveLabel(nextUnplaced || activeLabel);
    }, [activeLabel, placedPoints, pointsToPlace, valFromEvent, emitAnswer]);

    const handleSvgMove = useCallback((e) => {
        if (!activeLabel) return;
        setHoverVal(valFromEvent(e));
    }, [activeLabel, valFromEvent]);

    const handleSvgLeave = useCallback(() => setHoverVal(null), []);

    // ─── Réglage fin +/- (plein écran) : décale le point actif de ±step ───
    // ⚠ emitAnswer reste HORS de l'updater setState (effet de bord) — sinon React
    // peut le rejouer pendant un render et déclencher une boucle setState.
    const nudge = useCallback((dir) => {
        if (!activeLabel) return;
        const base = placedPoints[activeLabel] === undefined ? start : placedPoints[activeLabel];
        const v = snapToStep(base + dir * step);
        const next = { ...placedPoints, [activeLabel]: v };
        setPlacedPoints(next);
        emitAnswer(next);
    }, [activeLabel, placedPoints, start, step, snapToStep, emitAnswer]);

    // ─── Tout réinitialiser ───
    const handleReset = useCallback(() => {
        setPlacedPoints({});
        setHoverVal(null);
        setActiveLabel(pointsToPlace[0] || null);
        emitAnswer({});
    }, [pointsToPlace, emitAnswer]);

    // ─── Clic sur un label pill → simplement le SÉLECTIONNER (sans l'effacer) ───
    // Sélectionner un point déjà placé permet de l'AJUSTER (+/-, re-tap) sans
    // le supprimer. Pour tout recommencer : bouton « Réinitialiser ».
    const handleLabelClick = useCallback((label) => setActiveLabel(label), []);

    const lineColor = clair ? '#475569' : '#94a3b8';
    const textColor = clair ? '#1e293b' : '#cbd5e1';
    const formatValue = (val) => Number.isInteger(val) ? String(val) : String(val).replace('.', ',');

    // ─── Pills de labels (réutilisé vue normale + plein écran) ───
    const renderPills = (dark = false) => (
        <div className="flex flex-wrap gap-2">
            {pointsToPlace.map((label, i) => {
                const isPlaced = placedPoints[label] !== undefined;
                const isActive = activeLabel === label;
                const color = COLORS[i % COLORS.length];
                // En correction : la pastille porte le verdict de son point
                const e = etats ? etats.get(label) : null;
                const etat = e ? (e.juste ? 'juste' : 'faux') : null;
                const cls = etat
                    ? `border-2 ${t[etat]} cursor-default`
                    : isActive
                        ? (dark || !clair ? 'ring-2 ring-offset-2 ring-blue-400 bg-slate-700 text-white ring-offset-slate-900' : 'ring-2 ring-offset-2 ring-blue-400 bg-blue-50 text-blue-700')
                        : isPlaced
                            ? (dark || !clair ? 'bg-slate-700/50 text-slate-300 border border-slate-600' : 'bg-slate-100 text-slate-700 border border-slate-300')
                            : (dark || !clair ? 'bg-slate-700/30 text-slate-400 border border-dashed border-slate-600' : 'bg-white text-slate-500 border border-dashed border-slate-300');
                return (
                    <button
                        key={label}
                        onClick={() => handleLabelClick(label)}
                        disabled={Boolean(etat)}
                        data-etat={etat || undefined}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${cls}`}
                    >
                        <span className="w-3 h-3 rounded-full" style={{ background: etat ? CORRECTION[etat] : color }} />
                        {label}
                        {etat ? <span aria-hidden="true" className="ml-1 font-black">{marqueSigne(etat)}</span> : (isPlaced && <span className="text-[10px] text-emerald-400 ml-1">✓</span>)}
                    </button>
                );
            })}
            {!etats && Object.keys(placedPoints).length > 0 && (
                <button
                    onClick={handleReset}
                    className={`ml-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition ${dark || !clair ? 'text-slate-400 border-slate-600 hover:bg-slate-700 hover:text-slate-200' : 'text-slate-500 border-slate-300 hover:bg-slate-100 hover:text-slate-700'}`}
                    title="Effacer tous les points et recommencer"
                >
                    <RotateCcw size={12} /> Réinitialiser
                </button>
            )}
        </div>
    );

    // ─── Rendu de la droite SVG (paramétré normal/plein écran) ───
    const renderLine = (big) => {
        const s = big ? SIZES.big : SIZES.normal;
        const { H, lineY } = s;
        // Mêmes handlers tap dans les deux modes : place au clic + ghost au move.
        // En correction, la figure ne réagit plus
        const handlers = etats ? {} : {
            onClick: handleSvgClick,
            onPointerMove: handleSvgMove,
            onPointerLeave: handleSvgLeave,
        };
        const pts = pointsCorriges || placedPoints;

        return (
            <svg
                viewBox={`0 0 ${W} ${H}`}
                className="w-full select-none"
                preserveAspectRatio="xMidYMid meet"
                {...handlers}
            >
                {/* Ligne principale + flèche */}
                <line x1={padL} y1={lineY} x2={W - padR + 8} y2={lineY} stroke={lineColor} strokeWidth={s.line} />
                <polygon
                    points={`${W - padR + 15},${lineY} ${W - padR + 6},${lineY - 4} ${W - padR + 6},${lineY + 4}`}
                    fill={lineColor}
                />

                {/* Graduations */}
                {graduations.map((val, i) => {
                    const x = toX(val);
                    const isFirst = i === 0;
                    const isLast = i === graduations.length - 1;
                    const isRepere = reperesSet.has(Math.round(val * 1000));
                    const tH = (isFirst || isLast || isRepere) ? s.tickBig : s.tick;
                    return (
                        <g key={i}>
                            <line x1={x} y1={lineY - tH} x2={x} y2={lineY + tH} stroke={lineColor} strokeWidth={1} />
                            {(isFirst || isLast || isRepere) && (
                                <text x={x} y={lineY + tH + s.txt + 2} textAnchor="middle" fill={textColor} style={{ fontSize: s.txt }}>
                                    {formatValue(val)}
                                </text>
                            )}
                        </g>
                    );
                })}

                {/* Points placés (en correction : la couleur du verdict) */}
                {pointsToPlace.map((label, i) => {
                    const val = pts[label];
                    if (val === undefined) return null;
                    const x = toX(val);
                    const e = etats ? etats.get(label) : null;
                    const color = e ? CORRECTION[e.juste ? 'juste' : 'faux'] : COLORS[i % COLORS.length];
                    const isActive = big && activeLabel === label && !etats;
                    return (
                        <g key={`point-${label}`} pointerEvents="none">
                            {/* Anneau « poignée » sur le point actif en plein écran */}
                            {isActive && <circle cx={x} cy={lineY} r={s.r + 7} fill={color} opacity={0.18} />}
                            <circle cx={x} cy={lineY} r={s.r} fill={color} stroke="white" strokeWidth={big ? 2.5 : 1.5} />
                            <text x={x} y={lineY - s.r - 6} textAnchor="middle" fill={color} style={{ fontSize: s.lbl }} className="font-bold">
                                {label}
                            </text>
                        </g>
                    );
                })}

                {/* Correction : la position attendue d'un point faux, en pointillé, nommée sous la droite */}
                {etats ? pointsToPlace.map((label) => {
                    const e = etats.get(label);
                    if (!e || e.juste) return null;
                    const v = parseFloat(String(e.attendu).replace(',', '.'));
                    if (Number.isNaN(v)) return null;
                    const x = toX(v);
                    return (
                        <g key={`attendu-${label}`} pointerEvents="none" data-testid={`attendu-${label}`}>
                            <circle cx={x} cy={lineY} r={s.r + 1} fill="none" stroke={CORRECTION.juste} strokeWidth={2} strokeDasharray="3 2" />
                            <text x={x} y={lineY + s.tickBig + s.txt + 16} textAnchor="middle" fill={CORRECTION.juste} style={{ fontSize: s.lbl }} className="font-bold">
                                {label}
                            </text>
                        </g>
                    );
                }) : null}

                {/* Zone de clic élargie (vue normale uniquement) */}
                {!big && activeLabel && !etats && (
                    <line
                        x1={padL} y1={lineY - 10} x2={padL} y2={lineY + 10}
                        stroke="transparent" strokeWidth={usableW}
                        className="cursor-crosshair"
                    />
                )}

                {/* Point fantôme (position snappée) */}
                {activeLabel && !etats && hoverVal !== null && (() => {
                    const ghostColor = COLORS[Math.max(0, pointsToPlace.indexOf(activeLabel)) % COLORS.length];
                    const gx = toX(hoverVal);
                    return (
                        <g pointerEvents="none" opacity={0.55}>
                            <circle cx={gx} cy={lineY} r={s.r} fill={ghostColor} stroke="white" strokeWidth={big ? 2.5 : 1.5} strokeDasharray="2 2" />
                        </g>
                    );
                })()}
            </svg>
        );
    };

    return (
        <div className="space-y-3">
            {/* Instructions (masquées en correction) */}
            {etats ? null : (
            <p className={`text-xs sm:text-sm font-medium ${t.instructions} flex items-center gap-2`}>
                <span>📍</span> Clique sur un label puis sur la droite pour placer le point !
            </p>
            )}

            {renderPills(false)}

            {/* Bouton Agrandir (placement tactile confortable), jamais en correction */}
            {etats ? null : (
            <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition ${clair ? 'text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100' : 'text-blue-200 border-blue-500/40 bg-blue-500/10 hover:bg-blue-500/20'}`}
            >
                <Maximize2 size={14} /> Agrandir pour placer
            </button>
            )}

            {/* Droite graduée — vue normale */}
            <div
                className={`w-full rounded-xl border-2 transition-all ${clair ? 'bg-white' : ''} ${
                    activeLabel && !etats ? 'border-blue-500/50 cursor-crosshair' : (clair ? 'border-slate-300' : 'border-slate-600')
                }`}
            >
                {renderLine(false)}
            </div>

            {/* Vue plein écran : tap + réglage fin */}
            <PleinEcranInteractif open={isExpanded} onClose={() => setIsExpanded(false)} title="Place les points sur la droite">
                <div className="max-w-3xl mx-auto space-y-4">
                    <p className="text-slate-300 text-sm flex items-center gap-2">
                        <span>📍</span> Choisis un label, puis <b className="text-white">tape</b> sur la bonne graduation (ou ajuste avec −/+).
                    </p>

                    {renderPills(true)}

                    <div className="w-full rounded-xl border-2 border-slate-300 bg-white">
                        {renderLine(true)}
                    </div>

                    {/* Réglage fin du point actif */}
                    <div className="flex items-center justify-center gap-4">
                        <button
                            type="button"
                            onClick={() => nudge(-1)}
                            disabled={!activeLabel}
                            aria-label="Reculer le point d'un cran"
                            className="w-14 h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white border border-slate-600 transition"
                        >
                            <Minus className="w-6 h-6" />
                        </button>
                        <span className="text-slate-300 text-sm font-medium min-w-[7rem] text-center">
                            {activeLabel ? <>Ajuste le point <b className="text-white">{activeLabel}</b></> : 'Tous placés ✓'}
                        </span>
                        <button
                            type="button"
                            onClick={() => nudge(1)}
                            disabled={!activeLabel}
                            aria-label="Avancer le point d'un cran"
                            className="w-14 h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white border border-slate-600 transition"
                        >
                            <Plus className="w-6 h-6" />
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={() => setIsExpanded(false)}
                        className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 transition shadow-lg shadow-blue-500/30"
                    >
                        Terminé
                    </button>
                </div>
            </PleinEcranInteractif>
        </div>
    );
});

export default NumberLineInteractiveEleve;
